import { Box, CircularProgress, Modal, Typography } from "@mui/material";
import { ChangeEventHandler, FC, useEffect, useMemo, useState } from "react";
import CheckboxLabel from "../../core-components/checkbox-label/CheckboxLabel";
import CoreButtons from "../../core-components/button/CoreButtons";
import { useToast } from "../../../context/ToasterContext";
import DownloadIcon from "../../../assets/svg-fc/DownloadIcon";
import { BUTTON_COLOR, BUTTON_VARIANT } from "../../../types/coreButtonTypes";
import {
	iTermsContent,
	TermsViewerModeEnum,
	VendorRoleEnum,
} from "../../../types/termsTypes";
import { formatTermsDate } from "./termsFormat";
import { prepareTermsHtml } from "./termsHtml";
import { downloadTermsPdf } from "./termsPdf";
import "./termsDocumentViewer.css";

interface ITermsDocumentViewerProps {
	open: boolean;
	mode: TermsViewerModeEnum;
	content: iTermsContent | null;
	isLoading: boolean;
	error: string | null;
	/**
	 * Clamped effective date from the API. In blocking/notice mode this is the
	 * live published version's date; in read-only mode it's the specific
	 * (possibly superseded) version being viewed, not necessarily the live one.
	 * Omitted only if the caller couldn't resolve it.
	 */
	effectiveDate?: string;
	/**
	 * From the API's `canAccept`. A team member cannot bind the business, so they
	 * get the document without the consent footer rather than a control that
	 * would only fail server-side.
	 */
	canAccept?: boolean;
	/**
	 * Server role from `/terms/current`. Download PDF is Super Admin only —
	 * team members (`admin`) must not see the control even when the document is
	 * open for reading.
	 */
	role?: VendorRoleEnum | null;
	isAccepting?: boolean;
	acceptError?: string | null;
	/** Reminder state, for the team-member footer. Shared per vendor. */
	isReminderOnCooldown?: boolean;
	isReminding?: boolean;
	reminderRecipientName?: string | null;
	onClose: () => void;
	onAccept?: () => void;
	onRemindSuperAdmin?: () => void;
	/**
	 * Blocking mode has no close control, so once the viewer is open there is no
	 * way back to the blocking modal underneath it. Log out has to be reachable
	 * from here too, or a blocked vendor who opens the document is stuck reading
	 * it with no exit short of accepting.
	 */
	onLogout?: () => void;
}

const TermsDocumentViewer: FC<ITermsDocumentViewerProps> = ({
	open,
	mode,
	content,
	isLoading,
	error,
	effectiveDate,
	canAccept = true,
	role = null,
	isAccepting = false,
	acceptError = null,
	isReminderOnCooldown = false,
	isReminding = false,
	reminderRecipientName = null,
	onClose,
	onAccept,
	onRemindSuperAdmin,
	onLogout,
}) => {
	const { showToast } = useToast();
	const [consented, setConsented] = useState(false);
	const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);

	// A fresh consent decision every time the document is opened. Carrying a tick
	// across opens would let someone accept a document this session never showed.
	useEffect(() => {
		if (open) {
			setConsented(false);
		}
	}, [open, content?.version]);

	const isBlocking = mode === TermsViewerModeEnum.BLOCKING;
	const isReadOnly = mode === TermsViewerModeEnum.READ_ONLY;
	const canClose = !isBlocking;
	// The sidebar link is read-only by definition; a team member is read-only by
	// permission. Both see the document with no way to consent to it.
	const showConsentFooter = !isReadOnly && canAccept;
	const showTeamMemberFooter = !isReadOnly && !canAccept;

	const safeHtml = useMemo(() => prepareTermsHtml(content?.html ?? ""), [content?.html]);

	const canDownloadPdf =
		role === VendorRoleEnum.SUPER_ADMIN &&
		!!content?.version &&
		!!safeHtml.trim() &&
		!isLoading &&
		!error;

	const handleConsentChange: ChangeEventHandler<HTMLInputElement> = (event) => {
		setConsented(event.target.checked);
	};

	const handleDownloadPdf = async () => {
		if (!content?.version || isDownloadingPdf || !canDownloadPdf) {
			return;
		}

		setIsDownloadingPdf(true);
		try {
			await downloadTermsPdf({
				title: content.title || "Supplier Terms & Conditions",
				version: content.version,
				effectiveDate,
				html: safeHtml,
			});
		} catch {
			showToast("Could not download the PDF. Please try again.", undefined, "error");
		} finally {
			setIsDownloadingPdf(false);
		}
	};

	const downloadButton = canDownloadPdf ? (
		<button
			type="button"
			className="termsViewerDownload"
			aria-label={isDownloadingPdf ? "Preparing PDF" : "Download PDF"}
			disabled={isDownloadingPdf}
			onClick={() => {
				void handleDownloadPdf();
			}}>
			<DownloadIcon />
		</button>
	) : null;

	return (
		<Modal
			open={open}
			// In blocking mode this is a no-op, which is what makes ESC and the
			// backdrop inert — MUI routes both through onClose. No core-component
			// change needed.
			onClose={canClose ? onClose : () => {}}
			aria-labelledby="terms-viewer-title">
			<Box className={`termsDocumentViewer ${isBlocking ? "isBlocking" : ""}`}>
				<Box className="termsViewerHeader">
					<Box>
						<Typography id="terms-viewer-title" className="termsViewerTitle">
							{content?.title ?? "Supplier Terms & Conditions"}
						</Typography>
						{content?.version && (
							<Typography className="termsViewerMeta">
								Version {content.version}
								{effectiveDate ? ` · effective ${formatTermsDate(effectiveDate)}` : ""}
							</Typography>
						)}
					</Box>

					<Box className="termsViewerHeaderActions">
						{downloadButton}

						{canClose && (
							<button type="button" className="termsViewerClose" aria-label="Close" onClick={onClose}>
								{/* Matches the inline cross used by ModalHeader — the repo has no icon library. */}
								<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
									<path
										d="M13 13L1 1"
										stroke="#2C263F"
										strokeWidth="2"
										strokeLinecap="round"
										strokeLinejoin="round"
									/>
									<path
										d="M13 1L1 13"
										stroke="#2C263F"
										strokeWidth="2"
										strokeLinecap="round"
										strokeLinejoin="round"
									/>
								</svg>
							</button>
						)}
					</Box>
				</Box>

				<Box className="termsViewerBody">
					{isLoading && (
						<Box className="termsViewerCentered">
							<CircularProgress size={28} />
						</Box>
					)}

					{!isLoading && error && (
						<Box className="termsViewerCentered">
							<Typography className="termsViewerError">{error}</Typography>
						</Box>
					)}

					{!isLoading && !error && (
						// Sanitized twice: once by Baqaal into a frozen per-version
						// snapshot, once again here. See termsHtml.ts.
						<div className="termsContent" dangerouslySetInnerHTML={{ __html: safeHtml }} />
					)}
				</Box>

				{showTeamMemberFooter && (
					<Box className="termsViewerFooter">
						<Typography className="termsViewerNotice">
							Only your <strong>Super Admin</strong> can accept these terms on behalf of the business.
						</Typography>

						{reminderRecipientName && (
							<Box>
								<Typography className="termsViewerReminderSent">
									✓ Reminder sent to {reminderRecipientName}
								</Typography>
								<Typography className="termsViewerReminderHint">
									You can send another reminder in 24 hours.
								</Typography>
							</Box>
						)}

						<Box className="termsViewerActions">
							{isBlocking && (
								<CoreButtons
									buttonText="Log out"
									variant={BUTTON_VARIANT.OUTLINED}
									color={BUTTON_COLOR.INHERIT}
									coreBtnPill={true}
									className="termsLogoutButton"
									onclick={() => onLogout?.()}
								/>
							)}

							{!reminderRecipientName && (
								<CoreButtons
									buttonText="Remind Super Admin"
									variant={BUTTON_VARIANT.CONTAINED}
									color={BUTTON_COLOR.PRIMARY}
									coreBtnPill={true}
									disabled={isReminderOnCooldown || isReminding}
									onclick={() => onRemindSuperAdmin?.()}
								/>
							)}
						</Box>
					</Box>
				)}

				{showConsentFooter && (
					<Box className="termsViewerFooter">
						<CheckboxLabel
							label="I have read and agree to the Supplier Terms & Conditions"
							className="form-group termsConsentCheckbox"
							checked={consented}
							onChange={handleConsentChange}
						/>

						{acceptError && <Typography className="termsViewerError">{acceptError}</Typography>}

						<Box className="termsViewerActions">
							{/* Blocking mode has no close control, so Log out lives beside Accept. */}
							{isBlocking && (
								<CoreButtons
									buttonText="Log out"
									variant={BUTTON_VARIANT.OUTLINED}
									color={BUTTON_COLOR.INHERIT}
									coreBtnPill={true}
									className="termsLogoutButton"
									onclick={() => onLogout?.()}
								/>
							)}

							<CoreButtons
								buttonText={isBlocking ? "Accept" : "Accept now"}
								variant={BUTTON_VARIANT.CONTAINED}
								color={BUTTON_COLOR.PRIMARY}
								coreBtnPill={true}
								disabled={!consented || isAccepting || !!error}
								onclick={() => onAccept?.()}
							/>
						</Box>
					</Box>
				)}

			</Box>
		</Modal>
	);
};

export default TermsDocumentViewer;
